<template>
  <div class="righty-inline">
    <!-- Input Area -->
    <div class="relative w-full">
      <div
        class="glow-button"
        :class="{ 'opacity-25': minimal, 'opacity-40': !minimal }"
      ></div>
      <div
        class="relative z-20 w-full rounded-md border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-700"
        :class="[minimal ? 'min-h-16 p-2' : 'p-4']"
        :style="{ minHeight: minHeight || (minimal ? undefined : '8rem') }"
      >
        <!-- Righty avatar and label -->
        <div class="absolute -left-3 -top-3 z-30 flex items-center gap-2">
          <div class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-200 p-0.5 shadow-lg ring-2 ring-white dark:bg-slate-700 dark:ring-slate-700">
            <img src="/img/righty-light.jpg" alt="Righty AI" class="h-full w-full rounded-full object-cover dark:hidden" />
            <img src="/img/righty-dark.jpg" alt="Righty AI" class="hidden h-full w-full rounded-full object-cover dark:block" />
          </div>
          <span class="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-300">
            Powered by Righty
          </span>
        </div>
        <textarea
          ref="textareaRef"
          v-model="instructions"
          class="max-h-[400px] w-full resize-none overflow-y-auto border-none pr-12 text-lg text-brand-text placeholder:text-brand-text/40 focus:outline-none focus:ring-0 dark:bg-slate-700 dark:text-white/80"
          :class="[minimal ? 'min-h-[40px] text-sm' : '']"
          :style="{ minHeight: textareaMinHeight || (minimal ? undefined : '112px') }"
          :placeholder="placeholder"
          @input="autoGrow"
          @keydown.meta.enter="handleGenerate"
          @keydown.ctrl.enter="handleGenerate"
        />
      </div>
      <div class="absolute z-30 flex justify-end" :class="minimal || minHeight ? 'bottom-2 right-2' : 'bottom-4 right-4'">
        <Button
          :disabled="isGenerating || (!allowEmptySubmit && !instructions.trim())"
          :loading="isGenerating"
          :label="isGenerating ? 'Generating...' : buttonLabel"
          severity="ai"
          :size="minimal || minHeight ? 'small' : undefined"
          @click="handleGenerate"
        />
      </div>
    </div>

    <!-- Streaming Response (when streaming is enabled) -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
    >
      <div
        v-if="showResponse && streamingContent"
        class="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800"
      >
        <div
          class="prose prose-sm max-w-none dark:prose-invert"
          v-html="renderedContent"
        />
      </div>
    </Transition>

    <!-- Plan Card (when plan received and not auto-executing) -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
    >
      <div
        v-if="pendingPlan && !autoExecute"
        class="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-900/20"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1">
            <div class="flex items-center gap-2">
              <div class="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                <SparklesIcon class="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {{ pendingPlan.title }}
              </h4>
            </div>
            <p
              v-if="pendingPlan.description"
              class="mt-2 text-sm text-slate-600 dark:text-slate-300"
            >
              {{ pendingPlan.description }}
            </p>
          </div>
          <Button
            label="Use This"
            severity="success"
            size="small"
            @click="handleUsePlan"
          />
        </div>
      </div>
    </Transition>

    <!-- Error Display -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
    >
      <div
        v-if="error"
        class="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20"
      >
        <p class="text-sm text-red-600 dark:text-red-400">
          {{ error }}
        </p>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { SparklesIcon } from '@heroicons/vue/24/outline'
import { marked } from 'marked'
import { useRightyInline, type InlineTaskType } from '../composables/useRightyInline'
import type { PlanAction } from '../types'

const props = withDefaults(
  defineProps<{
    /** The task type to execute (required unless emitOnly is true) */
    taskType?: InlineTaskType
    /** Placeholder text for the textarea */
    placeholder?: string
    /** Initial value for the textarea (pre-filled text that user can edit) */
    initialValue?: string
    /** Button label */
    buttonLabel?: string
    /** Additional context to pass to the AI */
    context?: Record<string, any>
    /** Whether to auto-execute plans/configs without user confirmation */
    autoExecute?: boolean
    /** Whether to show streaming response content */
    showResponse?: boolean
    /** Whether to use minimal styling (compact mode) */
    minimal?: boolean
    /** When true, just emit 'submit' with the input text without calling the Righty API */
    emitOnly?: boolean
    /** When true, clear input after submit (only applies when emitOnly is true) */
    clearOnSubmit?: boolean
    /** Whether currently generating (for external control in emitOnly mode) */
    currentlyGenerating?: boolean
    /** When true, allow submitting with empty content (content becomes optional guidance) */
    allowEmptySubmit?: boolean
    /** Custom minimum height for the container (e.g. '4rem', '80px') */
    minHeight?: string
  }>(),
  {
    placeholder: 'Describe what you want to create...',
    initialValue: '',
    buttonLabel: 'Generate',
    autoExecute: false,
    showResponse: true,
    minimal: false,
    emitOnly: false,
    clearOnSubmit: true,
    currentlyGenerating: false,
    allowEmptySubmit: false
  }
)

const emit = defineEmits<{
  /** Emitted when a plan is ready (either for display or auto-execution) */
  (e: 'plan', plan: PlanAction): void
  /** Emitted when the user clicks "Use This" on a plan */
  (e: 'usePlan', plan: PlanAction): void
  /** Emitted when any result is received */
  (e: 'result', result: any): void
  /** Emitted when generation starts */
  (e: 'generating'): void
  /** Emitted when generation completes */
  (e: 'complete'): void
  /** Emitted in emitOnly mode with the user's input text */
  (e: 'submit', instruction: string): void
}>()

// Composable
const { execute, executeStreaming, isGenerating: internalIsGenerating, streamingContent, result, error, reset } =
  useRightyInline()

// Combined isGenerating state (internal + external control)
const isGenerating = computed(() => internalIsGenerating.value || props.currentlyGenerating)

// Derive textarea min-height from container minHeight prop (subtract padding)
const textareaMinHeight = computed(() => {
  if (!props.minHeight) return undefined
  const match = props.minHeight.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/)
  if (!match) return undefined
  const [, value, unit] = match
  const num = parseFloat(value)
  // Subtract ~2rem for container padding
  if (unit === 'rem' || unit === 'em') return `${Math.max(num - 2, 1)}${unit}`
  if (unit === 'px') return `${Math.max(num - 32, 16)}px`
  return undefined
})

// Local state
const instructions = ref(props.initialValue)
const textareaRef = ref<HTMLTextAreaElement>()
const pendingPlan = ref<PlanAction | null>(null)

// Computed
const renderedContent = computed(() => {
  return renderMarkdown(streamingContent.value)
})

// Methods
function renderMarkdown(content: string): string {
  if (!content) return ''
  return marked.parse(content, { async: false }) as string
}

function autoGrow() {
  const textarea = textareaRef.value
  if (!textarea) return
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

async function handleGenerate() {
  if (isGenerating.value) return
  if (!props.allowEmptySubmit && !instructions.value.trim()) return

  // Emit-only mode: just emit the input text without calling Righty API
  if (props.emitOnly) {
    emit('submit', instructions.value)
    if (props.clearOnSubmit) {
      clearInput()
    }
    return
  }

  // Validate taskType is provided when not in emitOnly mode
  if (!props.taskType) {
    console.error('RightyInline: taskType is required when emitOnly is false')
    return
  }

  // Reset previous results
  pendingPlan.value = null
  reset()

  emit('generating')

  // Use streaming if showResponse is enabled
  if (props.showResponse) {
    await executeStreaming({
      taskType: props.taskType,
      instruction: instructions.value,
      context: props.context
    })
  } else {
    await execute({
      taskType: props.taskType,
      instruction: instructions.value,
      context: props.context
    })
  }

  // Handle the result
  if (result.value?.success) {
    emit('result', result.value)

    if (result.value.plan) {
      emit('plan', result.value.plan)

      // Auto-use question_ideas plans (parent handles display with QuestionIdeaSelector)
      if (result.value.plan.resourceType === 'question_ideas') {
        emit('usePlan', result.value.plan)
        // Don't clear input - user might want to regenerate
      } else if (props.autoExecute) {
        emit('usePlan', result.value.plan)
        clearInput()
      } else {
        pendingPlan.value = result.value.plan
      }
    }
  }

  emit('complete')
}

function handleUsePlan() {
  if (pendingPlan.value) {
    emit('usePlan', pendingPlan.value)
    clearInput()
  }
}

function clearInput() {
  instructions.value = ''
  pendingPlan.value = null
  reset()
  autoGrow()
}

/**
 * Regenerate with the same instructions.
 * Useful for "Generate More Ideas" functionality.
 */
async function regenerate() {
  if (!instructions.value.trim() || isGenerating.value) return

  // Reset previous results but keep instructions
  pendingPlan.value = null
  reset()

  emit('generating')

  // Use streaming if showResponse is enabled
  if (props.showResponse) {
    await executeStreaming({
      taskType: props.taskType,
      instruction: instructions.value,
      context: props.context
    })
  } else {
    await execute({
      taskType: props.taskType,
      instruction: instructions.value,
      context: props.context
    })
  }

  // Handle the result
  if (result.value?.success) {
    emit('result', result.value)

    if (result.value.plan) {
      emit('plan', result.value.plan)

      // Auto-use question_ideas plans (parent handles display with QuestionIdeaSelector)
      if (result.value.plan.resourceType === 'question_ideas') {
        emit('usePlan', result.value.plan)
        // Don't clear input - user might want to regenerate
      } else if (props.autoExecute) {
        emit('usePlan', result.value.plan)
        clearInput()
      } else {
        pendingPlan.value = result.value.plan
      }
    }
  }

  emit('complete')
}

// Expose methods for parent components
defineExpose({
  clearInput,
  reset,
  regenerate,
  instructions
})
</script>

<style scoped>
.glow-button {
  &::before {
    content: '';
    background-image: linear-gradient(
      45deg,
      #d6d1d1,
      #f2e685,
      #fff,
      #e494bf,
      #63deb9,
      #fb9ccf,
      #458edf,
      #a96de5,
      #6ab2dc,
      #e894c1,
      #fff,
      #d6d1d1
    );
    position: absolute;
    top: -2px;
    left: -2px;
    background-size: 400%;
    z-index: 1;
    filter: blur(5px);
    width: calc(100% + 4px);
    height: calc(100% + 4px);
    animation: glowing 20s linear infinite;
    opacity: 1;
    transition: opacity 0.3s ease-in-out;
    border-radius: 0.375rem;
  }

  &:after {
    z-index: 2;
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    border-radius: 0.375rem;
    transition: all 225ms ease;
    background-color: #ffffffe6;
  }
}

@keyframes glowing {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 400% 0;
  }
}

:deep(.dark) .glow-button:after {
  background-color: rgba(30, 41, 59, 0.9);
}
</style>
